import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { serverMessage } from "@/lib/serverMessage";
import { setCheckoutInvoiceDetails } from "@/services/certificatePaymentsService";

export interface CheckoutInvoiceFieldsProps {
  checkoutId: string;
  /** What the checkout already carries. */
  initial: {
    includeRut: boolean;
    rutDigits: string | null;
    legalName: string | null;
    address: string | null;
  };
  /** The consumer's remembered identity, offered as a prefill. Never applied on its own. */
  suggestion: { rutDigits: string; legalName: string; address: string } | null;
  /** Locked once the payment is closed — the CFE is issued off this data. */
  disabled?: boolean;
}

/**
 * What the consumer wants their factura to say, asked at the till.
 *
 * WHY HERE AND NOT IN THE WIZARD. The invoice question used to be answered
 * when the REQUEST was created, which is the wrong moment twice over: a
 * consumer deciding what to buy is not yet thinking about who the invoice is
 * for, and the same person can buy twice and want one invoice to their company
 * and one to themselves. The payment is where that choice actually belongs.
 *
 * RAZON SOCIAL AND ADDRESS ARE REQUIRED WITH A RUT, and that is law rather
 * than house style: Art. 124 Decreto 220/998 requires the buyer identified by
 * name and RUT on an e-Factura. Without them a 111 went out carrying the
 * consumer's personal display name and no address at all.
 *
 * Saved on its own call rather than with the payment: the two payment surfaces
 * are different shapes — a card submits a token, a transfer a receipt hours
 * later — and threading these four fields through both would duplicate the
 * validation and let the two copies disagree.
 */
export function CheckoutInvoiceFields({
  checkoutId,
  initial,
  suggestion,
  disabled = false,
}: CheckoutInvoiceFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const [includeRut, setIncludeRut] = useState(initial.includeRut);
  const [rutDigits, setRutDigits] = useState(initial.rutDigits ?? "");
  const [legalName, setLegalName] = useState(initial.legalName ?? "");
  const [address, setAddress] = useState(initial.address ?? "");
  const [remember, setRemember] = useState(false);
  const [saving, setSaving] = useState(false);
  /**
   * What is on the SERVER, not what is in the form. Any edit clears it, so a
   * modified-but-unsent form can never be mistaken for a persisted one — which
   * is the whole reason the hint below exists.
   */
  const [saved, setSaved] = useState(initial.includeRut);

  /** Every field edit invalidates the saved state. */
  function edit<T>(setter: (value: T) => void): (value: T) => void {
    return (value: T) => {
      setSaved(false);
      setter(value);
    };
  }

  const complete = rutDigits.replace(/\D/g, "").length === 12 && legalName.trim() !== "" && address.trim() !== "";

  function applySuggestion(): void {
    if (!suggestion) return;
    setRutDigits(suggestion.rutDigits);
    setLegalName(suggestion.legalName);
    setAddress(suggestion.address);
  }

  /**
   * What to tell the consumer after a successful write.
   *
   * 'saved'   — details were stored, the normal case when the box is ticked.
   * 'removed' — details that WERE on the server are gone, because they
   *             unticked a box they had previously saved. Worth confirming:
   *             something they had is no longer there.
   * 'silent'  — nothing the consumer put in was stored or removed. Unticking a
   *             box they never completed records a refusal server-side, which
   *             matters internally, but from their side nothing of theirs
   *             changed and the form collapsing is the feedback.
   */
  type SaveFeedback = "saved" | "removed" | "silent";

  async function save(
    nextIncludeRut: boolean,
    feedback: SaveFeedback = "saved",
  ): Promise<void> {
    setSaving(true);
    try {
      await setCheckoutInvoiceDetails(checkoutId, {
        includeRut: nextIncludeRut,
        ...(nextIncludeRut
          ? {
              rutDigits: rutDigits.replace(/\D/g, ""),
              legalName: legalName.trim(),
              address: address.trim(),
              rememberForFuture: remember,
            }
          : {}),
      });
      setSaved(true);
      if (feedback === "saved") {
        toast.success(t("pay.invoice.saved"));
      } else if (feedback === "removed") {
        toast.success(t("pay.invoice.removed"));
      }
    } catch (err) {
      setSaved(false);
      toast.error(serverMessage(err) ?? t("pay.invoice.saveFailed"));
      // Rethrown so the un-check handler can restore the box rather than
      // leaving the screen disagreeing with the server.
      throw err;
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">{t("pay.invoice.title")}</h2>

      <label className="mt-3 flex items-start gap-2.5">
        <Checkbox
          checked={includeRut}
          disabled={disabled || saving}
          onCheckedChange={(next) => {
            const on = next === true;
            setIncludeRut(on);
            /**
             * Turning it OFF is a complete answer on its own, so it saves
             * immediately; turning it ON still needs the three fields.
             *
             * AND IT PUTS THE BOX BACK IF THE SAVE FAILS. Leaving it unticked
             * would hide the whole form — including the unsaved hint, which
             * only renders inside it — while the server still held the RUT,
             * and the consumer would see no invoice details on a checkout that
             * was about to issue a CFE with them.
             */
            if (!on) {
              /**
               * `saved` is what is on the SERVER, so it answers the only
               * question that decides the message: did this consumer actually
               * have invoice details stored?
               *
               * Unticking a box they had completed REMOVES something, and is
               * confirmed. Unticking one they only toggled stores a refusal
               * and changes nothing of theirs — reporting that as "we saved
               * your invoice details" described a save that never happened and
               * named data they had never entered.
               */
              void save(false, saved ? "removed" : "silent").catch(() => {
                setIncludeRut(true);
              });
            }
          }}
        />
        <span className="text-sm text-foreground">{t("pay.invoice.includeRut")}</span>
      </label>

      {includeRut ? (
        <div className="mt-4 space-y-3">
          {suggestion ? (
            <button
              type="button"
              onClick={applySuggestion}
              disabled={disabled || saving}
              className="rounded-md text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t("pay.invoice.useSaved", { name: suggestion.legalName })}
            </button>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="invoice-rut">{t("pay.invoice.rutLabel")}</Label>
            <Input
              id="invoice-rut"
              inputMode="numeric"
              autoComplete="off"
              placeholder={t("pay.invoice.rutPlaceholder")}
              value={rutDigits}
              disabled={disabled || saving}
              onChange={(e) =>
                edit(setRutDigits)(e.target.value.replace(/\D/g, "").slice(0, 12))
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invoice-legal-name">{t("pay.invoice.legalNameLabel")}</Label>
            <Input
              id="invoice-legal-name"
              autoComplete="organization"
              value={legalName}
              disabled={disabled || saving}
              onChange={(e) => edit(setLegalName)(e.target.value.slice(0, 255))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invoice-address">{t("pay.invoice.addressLabel")}</Label>
            <Input
              id="invoice-address"
              autoComplete="street-address"
              value={address}
              disabled={disabled || saving}
              onChange={(e) => edit(setAddress)(e.target.value.slice(0, 255))}
            />
          </div>

          <label className="flex items-start gap-2.5">
            <Checkbox
              checked={remember}
              disabled={disabled || saving}
              onCheckedChange={(next) => setRemember(next === true)}
            />
            <span className="text-xs text-muted-foreground">{t("pay.invoice.remember")}</span>
          </label>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || saving || !complete}
            onClick={() => {
              // The toast already happened inside save(); this catch only
              // keeps the rethrow from becoming an unhandled rejection.
              void save(true).catch(() => undefined);
            }}
            className="w-full"
          >
            {saving ? t("pay.invoice.saving") : saved ? t("pay.invoice.update") : t("pay.invoice.save")}
          </Button>

          {/* Stated plainly rather than enforced by blocking payment: the
              consumer can still pay without a RUT invoice, and a half-filled
              form must not be mistaken for a saved one. */}
          {!saved ? (
            <p className="text-xs text-muted-foreground">{t("pay.invoice.unsavedHint")}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
